import {
    Copy,
    DollarSign,
    Image as ImageIcon,
    Maximize2,
    Monitor,
    Music,
    Settings as SettingsIcon,
    Sparkles,
    Timer,
    Video,
    Volume2,
    VolumeX,
} from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import type { TFunction } from "i18next";

import type { LegacyArtifactType, LegacyGenerationJob } from "~/entities/chat";
import { useGenerationSettings } from "~/entities/model";
import { findModeConfig, getModelById, type GenerationMode } from "~/shared/data/chat-models";
import { pixelSizeLabel, useMediaPixelSize, type PixelSize } from "~/shared/lib/media-pixel-size";
import { resolutionLabel } from "~/shared/lib/resolution";
import { cn } from "~/shared/lib/utils";

const VIDEO_GENERATION_MODES = new Set([
    "text_to_video",
    "image_to_video",
    "frames",
    "motion_control",
    "upscale_video",
]);

function resolveArtifactType(job: LegacyGenerationJob): LegacyArtifactType {
    const first = job.artifacts[0];
    if (first) return first.artifactType;
    // Артефактов ещё нет (или их вовсе не будет) — определяем тип по режиму.
    if (job.requestPayload?.generationMode && VIDEO_GENERATION_MODES.has(job.requestPayload.generationMode)) {
        return "video";
    }
    return "image";
}

function artifactTypeColorClass(type: LegacyArtifactType): string {
    if (type === "video") return "text-violet-400/80";
    if (type === "audio") return "text-amber-400/80";
    return "text-blue-400/80";
}

interface MessageJobMetaRowProps {
    job: LegacyGenerationJob;
}

interface MetaPart {
    icon: React.ComponentType<{ className?: string }>;
    text: string;
    colorClass?: string;
}

/**
 * Подпись разрешения: ярлык заказа и фактический размер файла — вместе.
 *
 * Ярлык (`2K`) — это ось, по которой считалась цена; пиксели — то, что реально
 * пришло. Под одним ярлыком у разных платформ лежат разные размеры (у
 * higgsfield `2k` = 2752×1536, у нашего провайдера — свой, а у Kie ярлык вообще
 * не расшифрован), поэтому показывать только ярлык значило бы подписывать файл
 * чужим числом, а только размер — потерять связь с тем, что человек заказывал
 * и за что заплатил.
 *
 * Любой из двух может отсутствовать: у модели без оси разрешения ярлыка нет
 * вовсе, а размер не измерен, пока файл не доехал.
 */
function resolutionText(resolution: number | null | undefined, size: PixelSize | null): string | null {
    const label = resolution != null ? resolutionLabel(resolution) : null;
    const pixels = size ? pixelSizeLabel(size) : null;
    if (label && pixels) return `${label} (${pixels})`;
    return label ?? pixels;
}

/**
 * Оси запроса, влияющие на его цену: соотношение и разрешение описывают КАДР,
 * звук и размер пакета — ОБЪЁМ заказа. Возвращаются двумя группами, потому что в
 * строке они стоят по разные стороны от длительности.
 *
 * Читаются из СНИМКА, а не из текущих настроек чата — настройки с тех пор могли
 * смениться, а карточка обязана показывать то, с чем генерация реально ушла.
 * Исключение — фактический размер: он свойство доставленного файла, а не заказа.
 */
function requestAxisParts(
    payload: LegacyGenerationJob["requestPayload"],
    t: TFunction<"chat">,
    size: PixelSize | null,
): { frame: MetaPart[]; order: MetaPart[] } {
    const { aspectRatio, resolution, withSound, batchSize } = payload;
    const frame: MetaPart[] = [];
    const order: MetaPart[] = [];

    if (aspectRatio) frame.push({ icon: Maximize2, text: aspectRatio });
    const resolutionLine = resolutionText(resolution, size);
    if (resolutionLine) frame.push({ icon: Monitor, text: resolutionLine });
    // Звук показываем обоими состояниями: «без звука» — тоже осознанный выбор,
    // он меняет цену, и молчание о нём неотличимо от «модель звука не умеет».
    if (withSound != null) {
        order.push({
            icon: withSound ? Volume2 : VolumeX,
            text: t(withSound ? "messages.meta.soundOn" : "messages.meta.soundOff"),
        });
    }
    // Пакет — только когда он больше одного: единица есть у каждой генерации и
    // как признак не читается, а вот «×4» рядом с ценой объясняет её величину.
    if (batchSize != null && batchSize > 1) {
        order.push({ icon: Copy, text: t("messages.meta.batch", { count: batchSize }) });
    }

    return { frame, order };
}

function formatDuration(seconds: number | null): string | null {
    if (seconds == null) return null;
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds - m * 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatPrice(usd: string | null): string | null {
    if (!usd) return null;
    const n = Number.parseFloat(usd);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n < 10 ? `$${n.toFixed(2)}` : n < 100 ? `$${n.toFixed(1)}` : `$${Math.round(n)}`;
}

function artifactTypeIcon(type: LegacyArtifactType) {
    if (type === "video") return Video;
    if (type === "audio") return Music;
    return ImageIcon;
}

function artifactTypeLabelKey(type: LegacyArtifactType): string {
    if (type === "video") return "messages.meta.video";
    if (type === "audio") return "messages.meta.audio";
    return "messages.meta.image";
}

export function MessageJobMetaRow({ job }: MessageJobMetaRowProps) {
    const { t } = useTranslation("chat");
    const artifactType = resolveArtifactType(job);
    const TypeIcon = artifactTypeIcon(artifactType);
    const typeColorClass = artifactTypeColorClass(artifactType);
    const duration = formatDuration(job.durationSeconds);
    const price = formatPrice(job.totalMarketPriceUsd);

    const settingsQuery = useGenerationSettings();
    const model = getModelById(settingsQuery.data, job.modelName);
    const importedModelLabel = job.sourceModelName?.length ? job.sourceModelName : job.modelName;
    const modelLabel = job.isImported
        ? importedModelLabel
        : model
          ? `${model.brandLabel} / ${model.name}`
          : job.modelName.includes("/")
            ? job.modelName
                  .split("/")
                  .map((p, i) => (i === 0 ? p[0]!.toUpperCase() + p.slice(1) : p))
                  .join(" / ")
            : job.modelName;

    const modeKey = job.requestPayload.generationMode;
    // Лейбл режима — из каталога бэка (mode.title); модели/режима нет → сырой ключ.
    const modeLabel = modeKey ? (findModeConfig(model, modeKey as GenerationMode)?.title ?? modeKey) : null;

    // Меряем ПЕРВЫЙ артефакт: у пакета все результаты сделаны по одной оси, а
    // показать в одной строке разные размеры всё равно нечем. Импортированные
    // карточки используют лёгкие превью, поэтому не запрашиваем их оригиналы
    // только ради размеров.
    const first = job.artifacts[0];
    const originalUrlForMeasurement = job.isImported ? null : (first?.artifactUrl ?? null);
    const size = useMediaPixelSize(originalUrlForMeasurement, artifactType);
    const axes = requestAxisParts(job.requestPayload, t, size);

    const parts: MetaPart[] = [];
    parts.push({ icon: TypeIcon, text: t(artifactTypeLabelKey(artifactType)), colorClass: typeColorClass });
    parts.push({ icon: SettingsIcon, text: modelLabel });
    if (modeLabel) parts.push({ icon: Sparkles, text: modeLabel });
    parts.push(...axes.frame);
    if (duration) parts.push({ icon: Timer, text: duration });
    parts.push(...axes.order);
    if (price) parts.push({ icon: DollarSign, text: price });

    return (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
            {parts.map((part, i) => (
                <Fragment key={i}>
                    {i > 0 && <span className="text-zinc-700">·</span>}
                    <span className={cn("inline-flex items-center gap-1", part.colorClass)}>
                        <part.icon className="h-3.5 w-3.5" />
                        <span>{part.text}</span>
                    </span>
                </Fragment>
            ))}
        </div>
    );
}
